import React, { useState } from 'react';
import {
  Box,
  Typography,
  Avatar,
  IconButton,
  Tooltip,
  Fade,
  Popover,
} from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import CheckCircleOutlineOutlinedIcon from '@mui/icons-material/CheckCircleOutlineOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import AddReactionOutlinedIcon from '@mui/icons-material/AddReactionOutlined';
import { groupReactions, formatFullTime, QUICK_REACTIONS } from './chatUtils';
import FileAttachmentDisplay from './FileAttachmentDisplay';

const MessageBubble = ({
  message,
  isSelf,
  senderImage,
  senderName,
  showEyeIcon,
  downloadProgress,
  onDownload,
  onReact,
  currentUserId,
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const hasFile = !!message?.fileAttachment;
  const hasText = !!message?.message;

  const [isHovered, setIsHovered] = useState(false);
  const [pickerAnchor, setPickerAnchor] = useState(null);

  const grouped = groupReactions(message?.reactions, currentUserId);
  const hasReactions = Object.keys(grouped).length > 0;

  const renderMessageContent = () => {
    const text = message?.message;
    if (typeof text !== 'string') return text;
    try {
      const parts = text.split(/(\p{Emoji_Presentation}|\p{Extended_Pictographic})/gu);
      if (parts.length <= 1) return text;
      return parts.map((part, i) =>
        part && /(\p{Emoji_Presentation}|\p{Extended_Pictographic})/u.test(part) ? (
          <span key={i} className="msg-emoji">{part}</span>
        ) : (
          part
        )
      );
    } catch {
      return text;
    }
  };

  const handleOpenPicker = (e) => {
    e.stopPropagation();
    setPickerAnchor(e.currentTarget);
  };
  const handleClosePicker = () => setPickerAnchor(null);

  const handleReact = (emoji) => {
    if (onReact) onReact(message._id, emoji);
    handleClosePicker();
  };

  return (
    <Fade in>
      <Box
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        sx={{
          display: 'flex',
          flexDirection: isSelf ? 'row-reverse' : 'row',
          alignItems: 'flex-end',
          gap: 1,
          mb: hasReactions ? 0.5 : 1.5,
        }}
      >
        {!isSelf && (
          <Avatar
            src={senderImage || '/assets/ProfileAvatar.png'}
            alt={senderName}
            sx={{
              width: 32,
              height: 32,
              mb: 0.5,
              fontSize: '0.75rem',
              flexShrink: 0,
            }}
          >
            {!senderImage && senderName?.charAt(0)?.toUpperCase()}
          </Avatar>
        )}

        <Box
          sx={{
            maxWidth: { xs: '80%', sm: '65%' },
            display: 'flex',
            flexDirection: 'column',
            alignItems: isSelf ? 'flex-end' : 'flex-start',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 0.5 }}>
            {isSelf && (
              <Fade in={isHovered || Boolean(pickerAnchor)}>
                <Tooltip title="React">
                  <IconButton
                    size="small"
                    onClick={handleOpenPicker}
                    sx={{
                      color: 'text.secondary',
                      p: 0.4,
                      mb: 0.25,
                      flexShrink: 0,
                      '&:hover': { color: 'primary.main' },
                    }}
                  >
                    <AddReactionOutlinedIcon sx={{ fontSize: 17 }} />
                  </IconButton>
                </Tooltip>
              </Fade>
            )}

            <Box
              sx={{
                bgcolor: isSelf
                  ? 'primary.main'
                  : isDark
                  ? alpha(theme.palette.common.white, 0.08)
                  : alpha(theme.palette.grey[500], 0.1),
                color: isSelf ? 'white' : 'text.primary',
                borderRadius: isSelf ? '18px 18px 4px 18px' : '18px 18px 18px 4px',
                px: hasFile ? 1.5 : 2,
                py: 1.25,
                boxShadow: isSelf
                  ? `0 2px 8px ${alpha(theme.palette.primary.main, 0.3)}`
                  : `0 1px 4px ${alpha(theme.palette.common.black, isDark ? 0.3 : 0.06)}`,
                wordBreak: 'break-word',
              }}
            >
              {hasFile && (
                <Box sx={{ mb: hasText ? 1 : 0 }}>
                  <FileAttachmentDisplay
                    attachment={message.fileAttachment}
                    messageId={message._id}
                    isSelf={isSelf}
                    downloadProgress={downloadProgress}
                    onDownload={onDownload}
                  />
                </Box>
              )}
              {hasText && (
                <Typography
                  variant="body2"
                  component="span"
                  sx={{
                    display: 'inline',
                    fontSize: '1rem',
                    lineHeight: 1.5,
                    color: isSelf ? 'white' : 'text.primary',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                    '& .msg-emoji': { fontSize: '1.35rem', verticalAlign: 'middle' },
                  }}
                >
                  {renderMessageContent()}
                </Typography>
              )}
            </Box>

            {isSelf && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.25,
                  flexShrink: 0,
                  pb: 0.25,
                }}
              >
                {showEyeIcon ? (
                  <VisibilityOutlinedIcon
                    sx={{ fontSize: 14, color: 'primary.main' }}
                    titleAccess="Seen"
                  />
                ) : (
                  <CheckCircleOutlineOutlinedIcon
                    sx={{ fontSize: 14, color: 'text.secondary' }}
                    titleAccess="Sent"
                  />
                )}
              </Box>
            )}

            {!isSelf && (
              <Fade in={isHovered || Boolean(pickerAnchor)}>
                <Tooltip title="React">
                  <IconButton
                    size="small"
                    onClick={handleOpenPicker}
                    sx={{
                      color: 'text.secondary',
                      p: 0.4,
                      mb: 0.25,
                      flexShrink: 0,
                      '&:hover': { color: 'primary.main' },
                    }}
                  >
                    <AddReactionOutlinedIcon sx={{ fontSize: 17 }} />
                  </IconButton>
                </Tooltip>
              </Fade>
            )}
          </Box>

          <Typography
            variant="caption"
            sx={{
              color: 'text.secondary',
              mt: 0.4,
              px: 0.5,
              fontSize: '0.7rem',
            }}
          >
            {formatFullTime(message?.timestamp)}
          </Typography>

          {hasReactions && (
            <Box
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 0.5,
                mt: 0.5,
                mb: 1,
                justifyContent: isSelf ? 'flex-end' : 'flex-start',
              }}
            >
              {Object.entries(grouped).map(([emoji, { count, hasReacted, names }]) => (
                <Tooltip key={emoji} title={names.join(', ')} arrow>
                  <Box
                    onClick={() => handleReact(emoji)}
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 0.4,
                      px: 0.9,
                      py: 0.3,
                      borderRadius: 10,
                      cursor: 'pointer',
                      fontSize: '0.8rem',
                      userSelect: 'none',
                      border: '1.5px solid',
                      borderColor: hasReacted
                        ? 'primary.main'
                        : isDark
                        ? alpha(theme.palette.common.white, 0.15)
                        : alpha(theme.palette.common.black, 0.1),
                      bgcolor: hasReacted
                        ? isDark
                          ? alpha(theme.palette.primary.main, 0.25)
                          : alpha(theme.palette.primary.main, 0.1)
                        : isDark
                        ? alpha(theme.palette.common.white, 0.06)
                        : alpha(theme.palette.common.black, 0.04),
                      transition: 'all 0.15s ease',
                      '&:hover': {
                        borderColor: 'primary.main',
                        bgcolor: isDark
                          ? alpha(theme.palette.primary.main, 0.2)
                          : alpha(theme.palette.primary.main, 0.08),
                        transform: 'scale(1.08)',
                      },
                    }}
                  >
                    <span style={{ fontSize: '0.9rem', lineHeight: 1 }}>{emoji}</span>
                    <Typography
                      component="span"
                      sx={{
                        fontSize: '0.72rem',
                        fontWeight: 600,
                        color: hasReacted ? 'primary.main' : 'text.secondary',
                        lineHeight: 1,
                      }}
                    >
                      {count}
                    </Typography>
                  </Box>
                </Tooltip>
              ))}
            </Box>
          )}
        </Box>

        <Popover
          open={Boolean(pickerAnchor)}
          anchorEl={pickerAnchor}
          onClose={handleClosePicker}
          anchorOrigin={{
            vertical: 'top',
            horizontal: isSelf ? 'right' : 'left',
          }}
          transformOrigin={{
            vertical: 'bottom',
            horizontal: isSelf ? 'right' : 'left',
          }}
          PaperProps={{
            sx: {
              borderRadius: 3,
              p: 0.75,
              boxShadow: theme.shadows[6],
              border: `1px solid ${alpha(theme.palette.common.black, 0.08)}`,
            },
          }}
        >
          <Box sx={{ display: 'flex', gap: 0.25 }}>
            {QUICK_REACTIONS.map((emoji) => {
              const alreadyReacted = grouped[emoji]?.hasReacted;
              return (
                <Tooltip key={emoji} title={alreadyReacted ? 'Remove' : ''} arrow>
                  <IconButton
                    size="small"
                    onClick={() => handleReact(emoji)}
                    sx={{
                      fontSize: '1.2rem',
                      width: 36,
                      height: 36,
                      borderRadius: 2,
                      transition: 'transform 0.15s ease, background 0.15s ease',
                      bgcolor: alreadyReacted
                        ? alpha(theme.palette.primary.main, 0.12)
                        : 'transparent',
                      border: alreadyReacted
                        ? `1.5px solid ${alpha(theme.palette.primary.main, 0.4)}`
                        : '1.5px solid transparent',
                      '&:hover': {
                        transform: 'scale(1.25)',
                        bgcolor: alpha(theme.palette.primary.main, 0.08),
                      },
                    }}
                  >
                    {emoji}
                  </IconButton>
                </Tooltip>
              );
            })}
          </Box>
        </Popover>
      </Box>
    </Fade>
  );
};

export default MessageBubble;
